import { useCallback, useEffect, useState } from "react";
import { useAdminFetch } from "../lib/admin-api";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/Toast";

// PR #67 — Canonical ad-spend mapping console. The admin decides the canonical
// vendor-account -> license/chain bridge here; the decision stamps every
// historic core.ad_spend_fact row for that (vendor, vendor_account_id) and is
// recorded in storesync.match_decision. Brands layer their own overrides on top
// via #/brand/:slug/mappings/ad-spend.

interface PendingRow {
  vendor: string;
  vendor_account_id: string;
  fact_rows: number;
  pending_rows: number;
  confirmed_rows: number;
  first_period: string | null;
  last_period: string | null;
  total_spend_usd: string | number | null;
  brand_account: string | null;
  vendor_display_name: string | null;
  vendor_state: string | null;
  vendor_chain_hint: string | null;
  canonical_license_number: string | null;
  match_confidence: string | null;
  match_method: string | null;
  match_score: number | null;
  canonical_license_dba: string | null;
  canonical_license_name: string | null;
  canonical_license_city: string | null;
  canonical_chain_id: string | null;
  canonical_chain_name: string | null;
}

interface StoreHit {
  license_number: string;
  name: string | null;
  city: string | null;
  license_type: string | null;
  chain_id: string | null;
  chain_name: string | null;
  sources: string[] | null;
}
interface ChainHit {
  chain_id: string;
  display_name: string;
  canonical_root: string | null;
  member_count: number;
}

type VendorFilter = "all" | "dutchie" | "iheartjane" | "surfside";
const VENDOR_FILTERS: VendorFilter[] = ["all", "dutchie", "iheartjane", "surfside"];

function money(v: string | number | null): string {
  if (v == null) return "—";
  const n = typeof v === "number" ? v : parseFloat(v);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function rowKey(r: { vendor: string; vendor_account_id: string }): string {
  return `${r.vendor}::${r.vendor_account_id}`;
}

export default function AdminMappingsAdSpend() {
  const adminFetch = useAdminFetch();
  const toast = useToast();

  const [vendor, setVendor] = useState<VendorFilter>("all");
  const [rows, setRows] = useState<PendingRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  // Store-resolution cockpit: re-run the dry-run matcher over unpromoted
  // accounts, then bulk-promote the unambiguous (auto-tier) suggestions.
  const [reso, setReso] = useState<{ vendor: string; tier: string; accounts: number }[] | null>(null);
  const [resoBusy, setResoBusy] = useState<null | "rematch" | "dry-run" | "execute">(null);

  // Confirm dialog state. license/chain are seeded from the canonical suggestion.
  const [confirmTarget, setConfirmTarget] = useState<PendingRow | null>(null);
  const [confirmLicense, setConfirmLicense] = useState("");
  const [confirmChainId, setConfirmChainId] = useState("");
  const [confirmNotes, setConfirmNotes] = useState("");
  const [confirming, setConfirming] = useState(false);

  // "Match a different store / chain" typeahead pickers inside the dialog.
  const [storeQ, setStoreQ] = useState("");
  const [storeHits, setStoreHits] = useState<StoreHit[]>([]);
  const [chainQ, setChainQ] = useState("");
  const [chainHits, setChainHits] = useState<ChainHit[]>([]);

  useEffect(() => {
    const q = storeQ.trim();
    if (!confirmTarget || q.length < 2) {
      setStoreHits([]);
      return;
    }
    let live = true;
    const t = setTimeout(async () => {
      try {
        const r = await adminFetch<{ results: StoreHit[] }>(`/api/admin/ad-spend/store-search?q=${encodeURIComponent(q)}`);
        if (live) setStoreHits(r.results || []);
      } catch {
        if (live) setStoreHits([]);
      }
    }, 200);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [storeQ, confirmTarget, adminFetch]);

  useEffect(() => {
    const q = chainQ.trim();
    if (!confirmTarget || q.length < 2) {
      setChainHits([]);
      return;
    }
    let live = true;
    const t = setTimeout(async () => {
      try {
        const r = await adminFetch<{ rows: ChainHit[] }>(`/api/admin/chains?q=${encodeURIComponent(q)}`);
        if (live) setChainHits(r.rows || []);
      } catch {
        if (live) setChainHits([]);
      }
    }, 200);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [chainQ, confirmTarget, adminFetch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = vendor === "all" ? "" : `?vendor=${vendor}`;
      const r = await adminFetch<{ rows: PendingRow[] }>(`/api/admin/ad-spend/mappings/pending${qs}`);
      setRows(r.rows || []);
    } catch (err) {
      toast.push(`Failed to load pending mappings: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [adminFetch, toast, vendor]);

  useEffect(() => {
    load();
  }, [load]);

  const rematch = async () => {
    setResoBusy("rematch");
    try {
      const r = await adminFetch<{
        rematched: number;
        errors: number;
        detection: { vendor: string; tier: string; accounts: number }[];
        surfside_chains_resolved?: number;
        surfside_facts_stamped?: number;
      }>(`/api/admin/ad-spend/rematch`, { method: "POST", body: JSON.stringify({}) });
      setReso(r.detection || []);
      const surf = r.surfside_chains_resolved
        ? ` · Surfside: ${r.surfside_chains_resolved} chain(s) resolved, ${r.surfside_facts_stamped ?? 0} fact row(s) stamped`
        : "";
      toast.push(
        `Re-matched ${r.rematched} account(s)${r.errors ? `, ${r.errors} error(s)` : ""}.${surf} Tiers (Dutchie/iHeartJane) below.`,
        "success"
      );
    } catch (err) {
      toast.push(`Re-match failed: ${(err as Error).message}`, "error");
    } finally {
      setResoBusy(null);
    }
  };

  const autoPromote = async (execute: boolean) => {
    setResoBusy(execute ? "execute" : "dry-run");
    try {
      const r = await adminFetch<{ mode: string; executed: boolean; auto_eligible: number; auto_promoted: number; review_available: number }>(
        `/api/admin/ad-spend/auto-promote`,
        { method: "POST", body: JSON.stringify({ execute, confirmed_by: "admin-ui" }) }
      );
      if (execute && !r.executed) {
        toast.push(`Env gate is OFF (STORESYNC_ADSPEND_AUTOPROMOTE_MODE). ${r.auto_eligible} auto-eligible, but nothing promoted.`, "error");
      } else if (execute) {
        toast.push(`Promoted ${r.auto_promoted} account(s). Cascaded onto fact rows.`, "success");
        await load();
      } else {
        toast.push(`Dry-run: ${r.auto_eligible} auto-eligible, ${r.review_available} need review. Env mode: ${r.mode}.`, "success");
      }
    } catch (err) {
      toast.push(`Auto-promote failed: ${(err as Error).message}`, "error");
    } finally {
      setResoBusy(null);
    }
  };

  const openConfirm = (r: PendingRow, opts?: { search?: boolean }) => {
    setConfirmTarget(r);
    setConfirmNotes("");
    setChainHits([]);
    if (opts?.search) {
      // Search-first: clear the suggestion so the store typeahead is active and
      // empty. The verbatim ad-spend name is shown locked above for reference.
      setConfirmLicense("");
      setConfirmChainId("");
      setStoreQ("");
      setChainQ("");
      setStoreHits([]);
    } else {
      // Confirm-the-suggestion: pre-fill from the matcher's canonical guess.
      setConfirmLicense(r.canonical_license_number ?? "");
      setConfirmChainId(r.canonical_chain_id ?? "");
      setStoreQ("");
      setChainQ("");
      setStoreHits([]);
    }
  };

  const submitConfirm = async () => {
    if (!confirmTarget) return;
    const license = confirmLicense.trim();
    const chainId = confirmChainId.trim();
    if (!license && !chainId) {
      toast.push("Confirm needs a license number and/or a chain id.", "error");
      return;
    }
    setConfirming(true);
    try {
      const r = await adminFetch<{ fact_rows_stamped: number }>(`/api/admin/ad-spend/mappings/decide`, {
        method: "POST",
        body: JSON.stringify({
          vendor: confirmTarget.vendor,
          vendor_account_id: confirmTarget.vendor_account_id,
          status: "confirmed",
          license_number: license || undefined,
          chain_id: chainId || undefined,
          notes: confirmNotes.trim() || undefined,
        }),
      });
      toast.push(`Confirmed — ${r.fact_rows_stamped} fact row(s) stamped`, "success");
      setConfirmTarget(null);
      await load();
    } catch (err) {
      toast.push(`Confirm failed: ${(err as Error).message}`, "error");
    } finally {
      setConfirming(false);
    }
  };

  const decide = async (r: PendingRow, status: "rejected" | "unmatchable") => {
    const label = status === "rejected" ? "Reject" : "Mark unmatchable";
    if (!window.confirm(`${label} ${r.vendor} / ${r.vendor_account_id}?`)) return;
    setBusyKey(rowKey(r));
    try {
      await adminFetch(`/api/admin/ad-spend/mappings/decide`, {
        method: "POST",
        body: JSON.stringify({
          vendor: r.vendor,
          vendor_account_id: r.vendor_account_id,
          status,
          rejected_license_number: status === "rejected" ? r.canonical_license_number ?? undefined : undefined,
        }),
      });
      toast.push(`${label === "Reject" ? "Rejected" : "Marked unmatchable"}`, "success");
      await load();
    } catch (err) {
      toast.push(`${label} failed: ${(err as Error).message}`, "error");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Ad-spend mappings</h2>
        <p className="text-sm text-gray-600 mt-1">
          Vendor accounts with ad-spend that still need a canonical license or chain decision. Confirming stamps the
          bridge onto every historic fact row for that account, across all tenants.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Store resolution</h3>
            <p className="text-xs text-gray-600 mt-0.5">
              Re-run the matcher over accounts that still lack a license, then bulk-promote the unambiguous
              (auto-tier) matches. Auto-promote requires <code>STORESYNC_ADSPEND_AUTOPROMOTE_MODE=on</code> in the
              environment; use Dry-run to preview counts safely.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={rematch} disabled={resoBusy !== null}>
            {resoBusy === "rematch" ? "Re-matching…" : "Re-match unpromoted"}
          </Button>
          <Button variant="secondary" onClick={() => autoPromote(false)} disabled={resoBusy !== null}>
            {resoBusy === "dry-run" ? "Checking…" : "Auto-promote (dry-run)"}
          </Button>
          <Button variant="secondary" onClick={() => autoPromote(true)} disabled={resoBusy !== null}>
            {resoBusy === "execute" ? "Promoting…" : "Auto-promote (execute)"}
          </Button>
        </div>
        {reso && (
          <div className="flex flex-wrap gap-2 pt-1">
            {reso.length === 0 ? (
              <span className="text-xs text-gray-500">No unpromoted accounts with candidates.</span>
            ) : (
              reso.map((t) => (
                <span
                  key={`${t.vendor}-${t.tier}`}
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    t.tier === "auto" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"
                  }`}
                >
                  {t.vendor} · {t.tier}: {t.accounts}
                </span>
              ))
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {VENDOR_FILTERS.map((v) => (
          <button
            key={v}
            onClick={() => setVendor(v)}
            className={`px-3 py-1 rounded-full text-xs font-medium border capitalize transition-colors ${
              vendor === v
                ? "bg-accent-500 text-white border-accent-500"
                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
            }`}
          >
            {v}
          </button>
        ))}
        <button
          onClick={load}
          className="ml-auto px-3 py-1 rounded-full text-xs font-medium border bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-600">No pending ad-spend mappings.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Vendor account</th>
                <th className="text-left px-4 py-2 font-medium">Canonical suggestion</th>
                <th className="text-right px-4 py-2 font-medium">Fact rows</th>
                <th className="text-right px-4 py-2 font-medium">Spend</th>
                <th className="text-left px-4 py-2 font-medium">Period</th>
                <th className="text-left px-4 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const k = rowKey(r);
                const busy = busyKey === k;
                return (
                  <tr key={k} className="border-t border-gray-100 align-top">
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900">{r.vendor_display_name || r.vendor_account_id}</div>
                      <div className="text-xs text-gray-500">
                        <span className="capitalize">{r.vendor}</span> · <span className="font-mono">{r.vendor_account_id}</span>
                        {r.vendor_state ? ` · ${r.vendor_state}` : ""}
                      </div>
                      {r.vendor_chain_hint && (
                        <div className="text-xs text-gray-400 mt-0.5">hint: {r.vendor_chain_hint}</div>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {r.canonical_license_number ? (
                        <div className="text-gray-700">
                          <span className="font-mono">{r.canonical_license_number}</span>
                          {r.canonical_license_name || r.canonical_license_dba
                            ? ` · ${r.canonical_license_name || r.canonical_license_dba}`
                            : ""}
                          {r.canonical_license_city ? ` (${r.canonical_license_city})` : ""}
                          {r.match_confidence && (
                            <span className="ml-1 text-xs text-gray-400">
                              [{r.match_confidence}
                              {r.match_score != null ? ` ${r.match_score}` : ""}]
                            </span>
                          )}
                        </div>
                      ) : r.canonical_chain_id ? (
                        <div className="text-gray-700">
                          chain: {r.canonical_chain_name || r.canonical_chain_id}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">no canonical match</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                      {r.fact_rows.toLocaleString()}
                      {r.pending_rows > 0 && (
                        <span className="block text-xs text-amber-600">{r.pending_rows} pending</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-700">{money(r.total_spend_usd)}</td>
                    <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
                      {r.first_period || "—"} → {r.last_period || "—"}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={busy || !r.canonical_license_number}
                          title={r.canonical_license_number ? "Approve the suggested store" : "No suggestion to confirm — use Search"}
                          onClick={() => openConfirm(r)}
                        >
                          Confirm
                        </Button>
                        <Button variant="primary" size="sm" disabled={busy} onClick={() => openConfirm(r, { search: true })}>
                          Search
                        </Button>
                        <Button variant="secondary" size="sm" disabled={busy} onClick={() => decide(r, "rejected")}>
                          Reject
                        </Button>
                        <Button variant="secondary" size="sm" disabled={busy} onClick={() => decide(r, "unmatchable")}>
                          {busy ? "…" : "Unmatchable"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {confirmTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => !confirming && setConfirmTarget(null)}
        >
          <div className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900">Confirm canonical mapping</h3>
            <p className="text-sm text-gray-600 mt-1">
              Match this ad-spend account to the correct store and/or chain. Your choice stamps both historic and future
              fact rows for this account.
            </p>

            {/* LOCKED — verbatim from the ad-spend platform. Never editable; it's
                the source-of-truth label we're matching FROM. */}
            <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-gray-400">
                Ad-spend store — from {confirmTarget.vendor}
              </div>
              <div className="text-sm font-medium text-gray-900">
                {confirmTarget.vendor_display_name || confirmTarget.vendor_account_id}
              </div>
              <div className="text-xs text-gray-500 font-mono">
                {confirmTarget.vendor_account_id}
                {confirmTarget.vendor_state ? ` · ${confirmTarget.vendor_state}` : ""}
                {confirmTarget.vendor_chain_hint ? ` · hint: ${confirmTarget.vendor_chain_hint}` : ""}
              </div>
            </div>
            {(confirmTarget.canonical_license_number || confirmTarget.canonical_chain_id) && (
              <div className="mt-3 rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-600">
                Matcher suggested:{" "}
                {confirmTarget.canonical_license_number ? (
                  <span>
                    {confirmTarget.canonical_license_name || confirmTarget.canonical_license_dba || (
                      <span className="font-mono">{confirmTarget.canonical_license_number}</span>
                    )}
                    {confirmTarget.canonical_license_city ? ` · ${confirmTarget.canonical_license_city}` : ""}
                    {confirmTarget.match_confidence
                      ? ` [${confirmTarget.match_confidence}${confirmTarget.match_score != null ? ` ${confirmTarget.match_score}` : ""}]`
                      : ""}
                  </span>
                ) : (
                  <span>chain: {confirmTarget.canonical_chain_name || confirmTarget.canonical_chain_id}</span>
                )}
              </div>
            )}

            {/* Store (license) picker — search the FUSED corpus (CA license DB +
                Where-to-Buy + GBP + Address Book + Customer Master) and pick the
                store to match to. Resolves to a selected license; never free text. */}
            <div className="mt-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600">
                  Search for store <span className="text-gray-400">(license · searches DCC + Where-to-Buy + GBP + more)</span>
                </span>
                {confirmLicense && (
                  <button
                    type="button"
                    className="text-xs text-gray-400 hover:text-gray-600"
                    onClick={() => {
                      setConfirmLicense("");
                      setStoreQ("");
                    }}
                  >
                    clear
                  </button>
                )}
              </div>
              {confirmLicense ? (
                <div className="mt-1 rounded-md border border-accent-300 bg-accent-50 px-2 py-1.5 text-sm text-gray-800">
                  {storeQ || confirmTarget.canonical_license_dba || "Selected"}{" "}
                  <span className="font-mono text-xs text-gray-500">{confirmLicense}</span>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={storeQ}
                    onChange={(e) => setStoreQ(e.target.value)}
                    placeholder="Type a store name, city, or license #…"
                    autoFocus
                    className="mt-1 w-full text-sm border border-gray-200 rounded-md px-2 py-1"
                  />
                  {storeHits.length > 0 && (
                    <ul className="mt-1 max-h-52 overflow-auto rounded-md border border-gray-200 divide-y divide-gray-100">
                      {storeHits.map((h) => (
                        <li key={h.license_number}>
                          <button
                            type="button"
                            onClick={() => {
                              setConfirmLicense(h.license_number);
                              setStoreQ(h.name ?? "");
                              setStoreHits([]);
                              // Auto-derive the chain from the license — never ask
                              // for a uuid. Only fill if the operator hasn't already
                              // chosen a chain explicitly.
                              if (h.chain_id && !confirmChainId) {
                                setConfirmChainId(h.chain_id);
                                setChainQ(h.chain_name ?? "");
                              }
                            }}
                            className="w-full text-left px-2 py-1.5 hover:bg-gray-50"
                          >
                            <span className="text-sm text-gray-800">
                              {h.name || "(unnamed)"}
                              {h.sources && h.sources.length > 0 && (
                                <span className="ml-1 align-middle">
                                  {h.sources.map((src) => (
                                    <span
                                      key={src}
                                      className={`ml-1 rounded px-1 py-0.5 text-[10px] font-medium ${
                                        src === "wtb" || src === "address_book"
                                          ? "bg-green-100 text-green-700"
                                          : src === "gbp"
                                          ? "bg-blue-100 text-blue-700"
                                          : "bg-gray-100 text-gray-500"
                                      }`}
                                      title={`name sourced from ${src}`}
                                    >
                                      {src}
                                    </span>
                                  ))}
                                </span>
                              )}
                            </span>
                            <span className="block text-xs text-gray-500 font-mono">
                              {h.license_number}
                              {h.city ? ` · ${h.city}` : ""}
                              {h.chain_name ? <span className="text-accent-600"> · {h.chain_name}</span> : ""}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>

            {/* Chain / parent-account picker — the account-level target for
                Surfside (and any chain-grain match). */}
            <div className="mt-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600">Chain / parent account (optional)</span>
                {confirmChainId && (
                  <button
                    type="button"
                    className="text-xs text-gray-400 hover:text-gray-600"
                    onClick={() => {
                      setConfirmChainId("");
                      setChainQ("");
                    }}
                  >
                    clear
                  </button>
                )}
              </div>
              {confirmChainId ? (
                <div className="mt-1 rounded-md border border-accent-300 bg-accent-50 px-2 py-1.5 text-sm text-gray-800">
                  {chainQ || confirmTarget.canonical_chain_name || "Selected chain"}{" "}
                  <span className="font-mono text-xs text-gray-500">{confirmChainId}</span>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={chainQ}
                    onChange={(e) => setChainQ(e.target.value)}
                    placeholder="Search chains by name…"
                    className="mt-1 w-full text-sm border border-gray-200 rounded-md px-2 py-1"
                  />
                  {chainHits.length > 0 && (
                    <ul className="mt-1 max-h-52 overflow-auto rounded-md border border-gray-200 divide-y divide-gray-100">
                      {chainHits.map((h) => (
                        <li key={h.chain_id}>
                          <button
                            type="button"
                            onClick={() => {
                              setConfirmChainId(h.chain_id);
                              setChainQ(h.display_name);
                              setChainHits([]);
                            }}
                            className="w-full text-left px-2 py-1.5 hover:bg-gray-50"
                          >
                            <span className="text-sm text-gray-800">{h.display_name}</span>
                            <span className="block text-xs text-gray-500">{h.member_count} location(s)</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
            <label className="mt-3 block text-xs text-gray-500">
              Notes (optional)
              <textarea
                value={confirmNotes}
                onChange={(e) => setConfirmNotes(e.target.value)}
                rows={2}
                className="mt-1 w-full text-sm border border-gray-200 rounded-md px-2 py-1"
              />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirmTarget(null)} disabled={confirming}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={submitConfirm}
                disabled={confirming || (!confirmLicense.trim() && !confirmChainId.trim())}
              >
                {confirming ? "Confirming…" : "Confirm mapping"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
